import { useMutation } from '@apollo/client'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button, FormControl, InputLabel, MenuItem, Row, Select, Stack, Text, useAlert } from '../../components'
import { validations } from '../../components/form'
import { mutations } from '../../graphql'
import { parseAndSetFormErrors } from '../../utils'

export function DeactivateEmailForm({ currentEmail, availableEmails, refetchEmails }) {
  const [emailDeactivated, setEmailDeactivated] = useState(false)
  const { register, handleSubmit, formState, setError, setValue, getValues, reset } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const secondaryEmails = availableEmails.filter((item) => currentEmail !== item.email)

  const { isSubmitting, errors = {} } = formState
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const [deactivateEmail] = useMutation(mutations.user.deactivateEmail, {
    onCompleted: () => {
      setEmailDeactivated(true)
      setAlert({ message: 'Email successfully deleted', severity: 'success' })
    },
    onError: (newErrors) => {
      parseAndSetFormErrors(newErrors, setError)
      setAlert({ message: `Email deletion failed: ${newErrors.message}`, severity: 'error' })
    },
  })

  const restartForm = () => {
    refetchEmails?.()
    reset()
    setEmailDeactivated(false)
  }

  const onSubmit = async (data) => {
    deactivateEmail({
      variables: { data },
    })
  }

  return (
    <form data-testid="deactivate-secondary-email-form" onSubmit={handleSubmit(onSubmit)}>
      {emailDeactivated ? (
        <Stack>
          <Text.H6>Email Deleted Successfully!</Text.H6>
          <Text.Body data-testid="dialogContent">Your email has now been deleted.</Text.Body>
          <Row justifyContent="flex-end" alignItems="center" m={4}>
            <Button data-testid="dialog-ok-button" color="info" onClick={restartForm}>
              OK
            </Button>
          </Row>
        </Stack>
      ) : (
        <Stack>
          <Text.H6 data-testid="dialogTitle">Delete Email</Text.H6>
          <Stack mx={2}>
            <Text.Body data-testid="dialogContent">
              Please note that deleting your email will prevent you from using it in the future.
            </Text.Body>
            <FormControl fullWidth>
              <InputLabel id="emailToDeactivateLabel">Email to Delete *</InputLabel>
              <Select
                data-testid="deactivate-email-select"
                value={getValues('emailToDeactivate')}
                defaultValue={secondaryEmails[0].email}
                onChange={(e) => {
                  setValue('emailToDeactivate', e.target.value, { shouldValidate: true })
                }}
                label="Email to Delete *"
                labelId="emailToDeactivateLabel"
                aria-label="Email to Delete *"
                error={!!errors.emailToDeactivate}
                helpertext={errors?.emailToDeactivate?.message}
                {...register('emailToDeactivate', { ...validations.required })}
              >
                {secondaryEmails.map((availableEmail) => (
                  <MenuItem key={availableEmail.id} value={availableEmail.email}>
                    {availableEmail.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Row justifyContent="flex-end" alignItems="center" m={4}>
            <Button color="info" onClick={() => reset()}>
              Reset
            </Button>
            <Button disabled={isSubmitting} type="submit" data-testid="deleteButton">
              Delete Email
            </Button>
          </Row>
          <Alert {...alertProps} />
        </Stack>
      )}
    </form>
  )
}
